/**
 * Content Package Downloads Editor component.
 *
 * Provides a UI for adding/removing content package downloads with target paths.
 * Used in sandbox creation to specify files to download into the sandbox.
 *
 * @module components/ui/ContentPackageDownloadsEditor
 */

import { useState } from 'react';
import { Link } from 'react-router';
import { Plus, X } from 'lucide-react';
import { Select } from './Select';
import { Input } from './Input';
import { Button } from './Button';
import type { ContentPackageResponse } from '@/types';

/** A content package download entry */
export interface ContentPackageDownloadEntry {
  contentPackageId: string;
  targetPath: string;
  action?: 'Download' | 'Mount';
}

export interface ContentPackageDownloadsEditorProps {
  /** Current list of content package downloads */
  value: ContentPackageDownloadEntry[];
  /** Called when the list changes */
  onChange: (downloads: ContentPackageDownloadEntry[]) => void;
  /** Available content packages */
  contentPackages: ContentPackageResponse[] | undefined;
  /** Whether content packages are loading */
  isLoading?: boolean;
  /** Whether the editor is disabled */
  disabled?: boolean;
  /** When true, always show the draft form instead of requiring a button click */
  alwaysShowForm?: boolean;
  /** Whether to show the action selector (Download/Mount). Defaults to true. */
  showAction?: boolean;
}

/**
 * Formats a content package for display in a select option.
 */
function formatContentPackageLabel(pkg: ContentPackageResponse): string {
  const name = pkg.labels?.['name'];
  const otherLabels = pkg.labels
    ? Object.entries(pkg.labels)
        .filter(([k]) => k !== 'name')
        .map(([k, v]) => `${k}=${v}`)
        .slice(0, 2)
        .join(', ')
    : '';
  const sizeKb = Math.round(pkg.size / 1024);

  let label = name || `${pkg.id.slice(0, 8)}...`;
  if (otherLabels) {
    label += ` [${otherLabels}]`;
  }
  label += ` (${sizeKb} KB)`;

  return label;
}

/**
 * Gets display info for a content package.
 */
function getDisplayInfo(
  contentPackageId: string,
  contentPackages: ContentPackageResponse[] | undefined
): { displayName: string; labelEntries: [string, string][] } {
  const pkg = contentPackages?.find((p) => p.id === contentPackageId);
  const displayName = pkg?.labels?.['name'] || `${contentPackageId.slice(0, 8)}...`;
  const labelEntries = pkg?.labels
    ? (Object.entries(pkg.labels).filter(([k]) => k !== 'name') as [string, string][])
    : [];
  return { displayName, labelEntries };
}

/**
 * Validates a Linux absolute path.
 */
function validateTargetPath(
  path: string
): { isValid: true; cleanedPath: string } | { isValid: false; error: string } {
  let targetPath = path.trim();

  // Ensure absolute path
  if (!targetPath.startsWith('/')) {
    targetPath = '/' + targetPath;
  }

  // Strip trailing slashes (but keep single /)
  while (targetPath.length > 1 && targetPath.endsWith('/')) {
    targetPath = targetPath.slice(0, -1);
  }

  if (targetPath === '/') {
    return { isValid: false, error: 'Cannot download to root /' };
  }
  if (/\0/.test(targetPath)) {
    return { isValid: false, error: 'Path contains invalid characters' };
  }
  if (/\/\//.test(targetPath)) {
    return { isValid: false, error: 'Path contains consecutive slashes' };
  }
  if (/[\s]/.test(targetPath)) {
    return { isValid: false, error: 'Path should not contain whitespace' };
  }

  return { isValid: true, cleanedPath: targetPath };
}

/**
 * Editor component for managing a list of content package downloads.
 */
export function ContentPackageDownloadsEditor({
  value,
  onChange,
  contentPackages,
  isLoading = false,
  disabled = false,
  alwaysShowForm = false,
  showAction = true,
}: ContentPackageDownloadsEditorProps) {
  const [draftPackageId, setDraftPackageId] = useState('');
  const [draftTargetPath, setDraftTargetPath] = useState('');
  const [draftAction, setDraftAction] = useState<'Download' | 'Mount'>('Download');
  const [targetPathError, setTargetPathError] = useState<string | undefined>();
  const [showDraftForm, setShowDraftForm] = useState(true);

  // Filter out already-added packages
  const availablePackages = contentPackages?.filter(
    (p) => !value.some((d) => d.contentPackageId === p.id)
  );
  const hasAvailable = availablePackages && availablePackages.length > 0;

  const handleAdd = () => {
    const validation = validateTargetPath(draftTargetPath);
    if (!validation.isValid) {
      setTargetPathError(validation.error);
      return;
    }

    setTargetPathError(undefined);
    onChange([
      ...value,
      {
        contentPackageId: draftPackageId,
        targetPath: validation.cleanedPath,
        action: draftAction === 'Download' ? undefined : draftAction,
      },
    ]);
    setDraftPackageId('');
    setDraftTargetPath('');
    setDraftAction('Download');
    if (!alwaysShowForm) {
      setShowDraftForm(false);
    }
  };

  const handleRemove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handleTargetPathBlur = () => {
    let cleaned = draftTargetPath.trim();
    if (cleaned && !cleaned.startsWith('/')) {
      cleaned = '/' + cleaned;
    }
    while (cleaned.length > 1 && cleaned.endsWith('/')) {
      cleaned = cleaned.slice(0, -1);
    }
    if (cleaned !== draftTargetPath) {
      setDraftTargetPath(cleaned);
    }
  };

  return (
    <div className="space-y-4">
      {/* Committed downloads */}
      {value.length > 0 && (
        <div className="space-y-2">
          {value.map((download, index) => {
            const { displayName, labelEntries } = getDisplayInfo(
              download.contentPackageId,
              contentPackages
            );
            return (
              <div
                key={index}
                className="flex items-center justify-between gap-3 px-3 py-2 bg-gray-50 dark:bg-[#1a1a1a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg"
              >
                <div className="flex items-center gap-3 min-w-0 flex-wrap">
                  <span className="font-medium text-sm text-gray-900 dark:text-white">
                    {displayName}
                  </span>
                  {labelEntries.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {labelEntries.slice(0, 3).map(([k, v]) => (
                        <span
                          key={k}
                          className="text-xs px-1.5 py-0.5 bg-gray-200 dark:bg-[#333] text-gray-600 dark:text-gray-400 rounded-sm"
                        >
                          {k}={v}
                        </span>
                      ))}
                      {labelEntries.length > 3 && (
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          +{labelEntries.length - 3} more
                        </span>
                      )}
                    </div>
                  )}
                  <span className="text-gray-400 dark:text-gray-500">→</span>
                  <code className="text-sm text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-[#222] px-1.5 py-0.5 rounded-sm truncate">
                    {download.targetPath}
                  </code>
                  {showAction && download.action === 'Mount' && (
                    <span className="text-xs px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-sm font-medium">
                      Mount
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleRemove(index)}
                  className="p-1 text-gray-400 hover:text-red-500 transition-colors shrink-0"
                  title="Remove content package"
                  disabled={disabled}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Add new download form */}
      {!hasAvailable && !isLoading ? (
        contentPackages === undefined || contentPackages.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No content packages available.{' '}
            <Link
              to="/content-packages"
              className="text-primary-600 dark:text-primary-400 hover:underline"
            >
              Create a content package
            </Link>{' '}
            first.
          </p>
        ) : value.length > 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            All available content packages have been added.
          </p>
        ) : null
      ) : showDraftForm ? (
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <Select
              options={
                availablePackages?.map((p) => ({
                  value: p.id,
                  label: formatContentPackageLabel(p),
                })) || []
              }
              value={draftPackageId}
              onChange={(e) => setDraftPackageId(e.target.value)}
              placeholder={isLoading ? 'Loading content packages...' : 'Select a content package'}
              disabled={disabled || isLoading}
            />
          </div>
          <div className="flex-1">
            <Input
              placeholder="/app/data"
              value={draftTargetPath}
              onChange={(e) => {
                setDraftTargetPath(e.target.value);
                if (targetPathError) setTargetPathError(undefined);
              }}
              onBlur={handleTargetPathBlur}
              error={targetPathError}
              disabled={disabled}
            />
          </div>
          {showAction && (
            <div className="w-28">
              <Select
                options={[
                  { value: 'Download', label: 'Download' },
                  { value: 'Mount', label: 'Mount' },
                ]}
                value={draftAction}
                onChange={(e) => setDraftAction(e.target.value as 'Download' | 'Mount')}
                disabled={disabled}
              />
            </div>
          )}
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              leftIcon={<Plus className="h-3.5 w-3.5" />}
              disabled={disabled || !draftPackageId || !draftTargetPath.trim()}
              onClick={handleAdd}
            >
              Add
            </Button>
            {!alwaysShowForm && (
              <button
                type="button"
                onClick={() => {
                  setShowDraftForm(false);
                  setDraftPackageId('');
                  setDraftTargetPath('');
                  setDraftAction('Download');
                  setTargetPathError(undefined);
                }}
                className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                title="Cancel"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      ) : (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => setShowDraftForm(true)}
          disabled={disabled}
        >
          <Plus className="h-3 w-3 mr-1" />
          Add Content Package
        </Button>
      )}
    </div>
  );
}
